import { CSSObject } from '@emotion/react';
import { theme } from 'styles';
import { mobile } from 'styles/media';

const { color, radius } = theme;

const field: CSSObject = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid transparent',
  borderRadius: radius.md,
  background: color.grey50,
  color: color.text,
  fontSize: 15,
  lineHeight: 1.5,
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  ':hover': {
    background: color.grey75,
  },
  ':focus': {
    outline: 'none',
    background: color.white,
    borderColor: color.primary,
  },
  '::placeholder': {
    color: color.grey300,
  },
};

const wrap: CSSObject = {
  width: '100%',
  maxWidth: 760,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const header: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
};

const headerMeta: CSSObject = {
  fontSize: 14,
  color: color.textSub,
  strong: { color: color.text, fontWeight: 700 },
};

const card = (active: boolean): CSSObject => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 24,
  background: color.white,
  borderRadius: radius.xl,
  boxShadow: active ? `inset 0 0 0 2px ${color.primary}` : 'none',
  transition: 'box-shadow 0.15s ease, opacity 0.15s ease',
  [mobile]: {
    padding: '20px 16px',
    borderRadius: radius.lg,
  },
});

const titleInput: CSSObject = {
  width: '100%',
  padding: 0,
  border: 'none',
  background: 'transparent',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: color.text,
  ':focus': { outline: 'none' },
  '::placeholder': { color: color.grey300 },
  [mobile]: { fontSize: 20 },
};

const titleHint: CSSObject = {
  fontSize: 13,
  color: color.textMuted,
};

const questionHead: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const handle: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  margin: '0 -4px 0 -8px',
  padding: 4,
  borderRadius: radius.sm,
  color: color.grey300,
  cursor: 'grab',
  ':hover': { color: color.grey600, background: color.grey75 },
  ':active': { cursor: 'grabbing' },
};

const questionNo: CSSObject = {
  fontSize: 14,
  fontWeight: 700,
  color: color.textSub,
};

const moveButtons: CSSObject = {
  display: 'flex',
  gap: 2,
  marginLeft: 'auto',
  marginRight: -8,
};

const requiredMark: CSSObject = {
  color: color.mainOrange,
  marginLeft: 2,
};

const chips: CSSObject = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
};

const chip = (active: boolean): CSSObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 34,
  padding: '0 12px',
  border: 'none',
  borderRadius: radius.pill,
  fontSize: 13,
  fontWeight: 600,
  color: active ? color.white : color.grey600,
  background: active ? color.primary : color.grey75,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  ':hover': active ? {} : { background: color.grey100 },
});

const questionTitle: CSSObject = {
  ...field,
  height: 52,
  padding: '0 16px',
  fontSize: 17,
  fontWeight: 600,
};

const description: CSSObject = {
  ...field,
  padding: '12px 16px',
  fontSize: 14,
  resize: 'none',
};

const optionList: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const optionRow: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const optionMarker: CSSObject = {
  flex: 'none',
  display: 'flex',
  color: color.grey300,
};

const optionInput: CSSObject = {
  ...field,
  flex: 1,
  minWidth: 0,
  height: 44,
  padding: '0 14px',
};

const iconButton: CSSObject = {
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  border: 'none',
  borderRadius: radius.sm + 2,
  background: 'transparent',
  color: color.grey400,
  cursor: 'pointer',
  ':hover:not(:disabled)': { background: color.grey75, color: color.grey700 },
  ':disabled': { color: color.grey200, cursor: 'not-allowed' },
};

const addOption: CSSObject = {
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 22,
  padding: '6px 8px',
  border: 'none',
  borderRadius: radius.sm,
  background: 'transparent',
  fontSize: 14,
  fontWeight: 600,
  color: color.info,
  cursor: 'pointer',
  ':hover': { background: color.infoBg },
};

const scaleRange: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  color: color.textSub,
  select: {
    ...field,
    width: 88,
    height: 44,
    padding: '0 12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const scaleLabel: CSSObject = {
  flex: 'none',
  width: 56,
  fontSize: 14,
  color: color.textSub,
};

const textPreview: CSSObject = {
  padding: '14px 16px',
  borderRadius: radius.md,
  border: `1px dashed ${color.grey200}`,
  fontSize: 14,
  color: color.grey400,
};

const questionFoot: CSSObject = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 4,
  paddingTop: 16,
  borderTop: `1px solid ${color.grey75}`,
};

const divider: CSSObject = {
  width: 1,
  height: 20,
  margin: '0 8px',
  background: color.grey100,
};

const toggle: CSSObject = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  fontWeight: 500,
  color: color.grey700,
  cursor: 'pointer',
  userSelect: 'none',
  input: {
    appearance: 'none',
    position: 'relative',
    width: 40,
    height: 24,
    margin: 0,
    borderRadius: radius.pill,
    background: color.grey200,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '::after': {
      content: '""',
      position: 'absolute',
      top: 2,
      left: 2,
      width: 20,
      height: 20,
      borderRadius: '50%',
      background: color.white,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
      transition: 'transform 0.2s ease',
    },
    ':checked': { background: color.info },
    ':checked::after': { transform: 'translateX(16px)' },
    ':focus-visible': { outline: `2px solid ${color.infoBg}` },
  },
};

const addQuestion: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  height: 56,
  border: `1.5px dashed ${color.grey200}`,
  borderRadius: radius.xl,
  background: 'transparent',
  fontSize: 15,
  fontWeight: 600,
  color: color.grey600,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  ':hover': {
    background: color.white,
    borderColor: color.grey300,
    color: color.text,
  },
  [mobile]: { borderRadius: radius.lg },
};

const actionBar: CSSObject = {
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  margin: '8px -8px 0',
  padding: '16px 8px 12px',
  background: `linear-gradient(to top, ${color.background} 75%, transparent)`,
  '> button': {
    height: 48,
    minWidth: 120,
    fontSize: 15,
    borderRadius: radius.md,
  },
  [mobile]: {
    '> button': { flex: 1 },
  },
};

const loadList: CSSObject = {
  width: 480,
  maxWidth: '100%',
  maxHeight: '60vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  [mobile]: { width: '100%' },
};

const loadHint: CSSObject = {
  marginBottom: 8,
  fontSize: 13,
  color: color.textMuted,
};

const loadItem: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '14px 12px',
  border: 'none',
  borderRadius: radius.md,
  background: 'transparent',
  fontSize: 15,
  fontWeight: 500,
  color: color.text,
  textAlign: 'left',
  cursor: 'pointer',
  svg: { flex: 'none', color: color.grey300 },
  ':hover': { background: color.grey50 },
};

const loadEmpty: CSSObject = {
  padding: '40px 0',
  textAlign: 'center',
  fontSize: 14,
  color: color.textMuted,
};

export {
  wrap,
  header,
  headerMeta,
  card,
  titleInput,
  titleHint,
  questionHead,
  handle,
  questionNo,
  requiredMark,
  moveButtons,
  chips,
  chip,
  questionTitle,
  description,
  optionList,
  optionRow,
  optionMarker,
  optionInput,
  iconButton,
  addOption,
  scaleRange,
  scaleLabel,
  textPreview,
  questionFoot,
  divider,
  toggle,
  addQuestion,
  actionBar,
  loadList,
  loadHint,
  loadItem,
  loadEmpty,
};
